import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Booking, Scope, ShipInterface } from '../../core/models';
import { BookingService } from '../../services/resources/booking.service';
import { DashboardFilterService } from '../../services/filter/dashboard-filter.service';
import {
  auditTime,
  combineLatest,
  filter,
  first,
  map,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { addDays, isSameDay, parseISO, startOfWeek } from 'date-fns';
import { colorMap } from '../../core/bookingParameters';
import { utcCalendarDay } from '../../core/booking-from.utils';
import { animate, style, transition, trigger } from '@angular/animations';
import { TruncateTextPipe } from '../../core/truncate-text.pipe';
import { LoadingBarrierService } from '../../services/resources/loading-barrier.service';
@Component({
  selector: 'app-shipcalendar-summary',
  imports: [CommonModule, TruncateTextPipe],
  standalone: true,
  template: `
    <ng-container *ngIf="shipSummaryMap$ | async as data">
      <ng-container *ngIf="ObjectKeys(data); else noBookings">
        <div class="px-2 pb-4" [@fadeIn]="'visible'">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
            >
              <div
                class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg"
              >
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-1 pr-3 text-left text-sm font-semibold text-gray-900 "
                      >
                        Company
                      </th>
                      <th
                        scope="col"
                        class="px-1 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Ship
                      </th>
                      <th
                        *ngFor="let day of week"
                        scope="col"
                        class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900"
                      >
                        {{ day | date: 'EEEEEE d.M' : '' : 'de-De' }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-400 bg-white">
                    <tr
                      *ngFor="let entry of data | keyvalue: keepOriginalOrder"
                      [ngStyle]="{
                        'background-color': getCompanyColors(entry.key),
                      }"
                    >
                      <td
                        class="whitespace-nowrap px-1 py-1 text-sm text-black truncate "
                      >
                        {{
                          getShipData(entry.key)?.companyName | truncateText: 15
                        }}
                      </td>
                      <td
                        class="whitespace-nowrap px-1 py-1 text-sm text-black"
                      >
                        {{ getShipData(entry.key)?.shipName }}
                      </td>
                      <td
                        *ngFor="let day of week"
                        class="whitespace-nowrap px-1 py-1 text-sm text-black"
                      >
                        <div class="flex flex-row justify-center">
                          <ng-container
                            *ngFor="
                              let booking of getBookingData(entry.value, day)
                            "
                          >
                            <div
                              [ngClass]="{
                                'text-columbus-blue-600':
                                  booking.accounting?.billing?.billed,
                              }"
                            >
                              <svg
                                class="size-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path
                                  d="M4 10.4V4C4 3.44772 4.44772 3 5 3H10V1H14V3H19C19.5523 3 20 3.44772 20 4V10.4L21.0857 10.7257C21.5974 10.8792 21.8981 11.4078 21.7685 11.9261L20.2516 17.9938C20.1682 17.9979 20.0844 18 20 18C18.3644 18 16.9122 17.2147 16 16.0005C15.0878 17.2147 13.6356 18 12 18C10.3644 18 8.91223 17.2147 8 16.0005C7.08777 17.2147 5.6356 18 4 18C3.91564 18 3.83178 17.9979 3.74845 17.9938L2.23152 11.9261C2.10195 11.4078 2.40262 10.8792 2.91431 10.7257L4 10.4ZM6 9.8L12 8L14.7541 8.82624L16.5627 9.36882L18 9.8V5H6V9.8ZM4 20C5.53671 20 6.93849 19.4223 8 18.4722C9.06151 19.4223 10.4633 20 12 20C13.5367 20 14.9385 19.4223 16 18.4722C17.0615 19.4223 18.4633 20 20 20H22V22H20C18.5429 22 17.1767 21.6104 16 20.9297C14.8233 21.6104 13.4571 22 12 22C10.5429 22 9.17669 21.6104 8 20.9297C6.82331 21.6104 5.45715 22 4 22H2V20H4Z"
                                ></path>
                              </svg>
                            </div>
                          </ng-container>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <ng-template #noBookings>
      <div class="flex justify-center items-center py-4 italic text-gray-400">
        No bookings this week
      </div>
    </ng-template>
  `,
  styles: ``,
  animations: [
    trigger('fadeIn', [
      transition('* => visible', [
        style({ opacity: 0 }),
        animate('500ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class ShipcalendarSummaryComponent implements OnInit, OnDestroy {
  @Input({ required: true }) ships!: ShipInterface[];

  bookingService = inject(BookingService);
  filterState = inject(DashboardFilterService);
  filterStateSub$: Subscription | undefined = undefined;

  shipSummaryMap: Record<string, Booking[]> = {};

  private destroy$ = new Subject<void>();
  private readonly barrier = inject(LoadingBarrierService);
  private scope: Scope = 'bootstrap';
  startDate: Date = new Date();
  week: Date[] = [];

  keepOriginalOrder = () => 0;

  shipSummaryMap$ = this.filterState.filters$.pipe(
    // only proceed when we have a date

    filter((fs) => !!fs?.savedDate),
    auditTime(0),
    switchMap((fs) => {
      this.barrier.register(this.scope, 'ship-summary');
      const date = parseISO(fs!.savedDate! as string);
      this.week = this.generateWeek(date);
      // build an array of observables [ { shipId, bookings }, ... ]
      const calls = this.ships.map((ship) =>
        this.bookingService
          .getBookings(date, ship.id)
          .pipe(map((bookings: Booking[]) => ({ shipId: ship.id, bookings }))),
      );
      // wait for all of them
      const inner$ = combineLatest(calls).pipe(
        map((results) =>
          results.reduce<Record<string, Booking[]>>(
            (acc, { shipId, bookings }) => {
              const filteredBookings = bookings.filter(
                (booking) => !booking.deleteFlag,
              );
              if (filteredBookings.length > 0) {
                acc[shipId] = filteredBookings;
              }

              return acc;
            },
            {},
          ),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

      inner$.pipe(first()).subscribe({
        next: () => this.barrier.markReady(this.scope, 'ship-summary'),
        error: () => this.barrier.markReady(this.scope, 'ship-summary'),
      });

      return inner$;
    }),
    takeUntil(this.destroy$),
  );

  ObjectKeys(data: Record<string, Booking[]>) {
    return Object.keys(data).length > 0;
  }

  generateWeek(date: Date) {
    let genWeek: Date[] = [];
    // Same week as the Firestore path: the UTC calendar day, not the browser-local one.
    const startDate = startOfWeek(utcCalendarDay(date), { weekStartsOn: 1 });
    genWeek.push(startDate);

    for (let i = 0; i < 6; i++) {
      genWeek.push(addDays(genWeek[i], 1));
    }

    return genWeek;
  }

  getCompanyColors(shipId: string) {
    const ship = this.getShipData(shipId);

    if (!ship) {
      return '#FFFFFF';
    }

    let companyColors = colorMap;

    return companyColors[ship.companyName];
  }

  getShipData(id: string): ShipInterface | undefined {
    return this.ships.find((ship) => ship.id === id);
  }

  getBookingData(bookings: Booking[], date: Date): Booking[] {
    let dateBookings: Booking[] = [];

    dateBookings = bookings.filter((booking) =>
      isSameDay(
        utcCalendarDay(new Date(booking.duration.startTime.seconds * 1000)),
        date,
      ),
    );

    return dateBookings;
  }

  log(val: any) {
    console.log(val, this.ships);
  }

  ngOnInit(): void {}
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
